/**
 * Offer domain enums + error codes (TO-339). The offer is an owned, persisted
 * entity in offer-service; these are its shared status/channel/artifact vocab and
 * the machine-readable error codes its HTTP surface returns.
 */

import type { AccessMode } from '../landing/landing.interface.js'

/**
 * Offer lifecycle status. Guarded transitions (see offer-service's status machine):
 * DRAFT/SEND_FAILED→SENDING (send); SENDING→SENT (artefact join) or SEND_FAILED
 * (render / snapshot failure, TO-464); SENT→{EXPIRED,REVOKED,CONVERTED};
 * DRAFT/SEND_FAILED/SENT→ERROR. ERROR is terminal.
 */
export enum OfferStatus {
	DRAFT = 'DRAFT',
	/** Send accepted; the render is in flight (TO-464). */
	SENDING = 'SENDING',
	/** The send's render or snapshot failed; re-sendable (TO-464). */
	SEND_FAILED = 'SEND_FAILED',
	SENT = 'SENT',
	EXPIRED = 'EXPIRED',
	REVOKED = 'REVOKED',
	CONVERTED = 'CONVERTED',
	ERROR = 'ERROR',
}

/**
 * A channel an offer can be sent through; a send names one or more (TO-465).
 * Declaration order is the canonical order of every channel list.
 */
export enum OfferChannel {
	EMAIL = 'EMAIL',
	SMS = 'SMS',
	WHATSAPP = 'WHATSAPP',
}

/** The artifact kinds offer-service orchestrates, named in offer.artifact_requested. */
export enum OfferArtifactType {
	LANDING = 'LANDING',
	PDF = 'PDF',
}

/**
 * An artifact's readiness on the offer DTO (TO-466): `READY` once its stored
 * reference is written back onto the offer row, `PENDING` until then. The storage
 * key itself never leaves offer-service.
 */
export enum OfferArtifactStatus {
	PENDING = 'PENDING',
	READY = 'READY',
}

/** Per-artifact readiness, as carried on every offer DTO. */
export interface OfferArtifacts {
	landing: OfferArtifactStatus
	pdf: OfferArtifactStatus
}

/**
 * Machine-readable error codes on offer-service's HTTP surface (raised as
 * `OfferRefusal`, mapped to 404/409/422). Customer/product-not-found on create reuse
 * the shared `NotFoundErrorCode`; an unresolvable legal text reuses the composer's
 * `ValidationErrorCode.TEXT_NOT_AVAILABLE` (TO-429). These are the offer-owned codes.
 */
export enum OfferErrorCode {
	OFFER_NOT_FOUND = 'OFFER_NOT_FOUND',
	OFFER_TRANSITION_NOT_ALLOWED = 'OFFER_TRANSITION_NOT_ALLOWED',
	OFFER_STATUS_CONFLICT = 'OFFER_STATUS_CONFLICT',
	/** A render of the offer is already in flight (SENDING, or a SENT re-version) — TO-464. */
	OFFER_SEND_IN_PROGRESS = 'OFFER_SEND_IN_PROGRESS',
	/** A product-set replace addressed an offer that is no longer a DRAFT (TO-123) → 409. */
	OFFER_NOT_EDITABLE = 'OFFER_NOT_EDITABLE',
}

/** Why the offer's last render did not publish (TO-464); surfaced as `renderFailure.code`. */
export enum OfferRenderFailureCode {
	LANDING_RENDER_FAILED = 'LANDING_RENDER_FAILED',
	PDF_RENDER_FAILED = 'PDF_RENDER_FAILED',
	SNAPSHOT_WRITE_FAILED = 'SNAPSHOT_WRITE_FAILED',
}

/** The offer's last render failure (TO-464); null once a newer render is requested. */
export interface OfferRenderFailure {
	code: OfferRenderFailureCode
	/** The version the failed render was producing. */
	renderVersion: number
	failedAt: Date
}

/**
 * The offer aggregate as owned by offer-service (TO-339). Artifact references
 * (`htmlKey`, `pdfKey`, `pdfCode`, `shortCode`) are null until each artifact
 * completes and is written back; `currentRenderId` is the render they belong to.
 * `customerId`, `createdBy` and `orgUnitId` are opaque cross-context uuids.
 */
export interface IOffer {
	/** Internal, app-owned id (primary key, uuid v7, globally unique). */
	offerId: string
	/** The tenant this offer belongs to (from the DB-resolved principal). */
	tenantId: string
	customerId: string
	/** The creating manager, from the DB-resolved principal. */
	createdBy: string
	/** Nullable; unpopulated for now (TO-412). */
	orgUnitId: string | null
	/** The locale the offer's legal texts were resolved in (BCP-47). */
	locale: string
	introduction: string | null
	/** The channels the offer was sent through, in declaration order; empty until send (TO-465). */
	sendChannels: OfferChannel[]
	status: OfferStatus
	/** Public landing access mode (ADR-0051); defaults NO_AUTH. */
	accessMode: AccessMode
	/**
	 * Salted hash of the sender's CODE for `AccessMode.CODE`; null for every other
	 * mode. Entity-internal — the plaintext never leaves offer-service and this hash
	 * is NOT serialized into the offer DTO.
	 */
	accessCodeHash: string | null
	/** The offer's latest requested render; the artifact write-back key. */
	currentRenderId: string
	/**
	 * Landing call-to-action (https-only href, validated at create/patch). Both null
	 * or both set: a minimal draft has none until Step 3 collects it (TO-125).
	 */
	ctaLabel: string | null
	ctaHref: string | null
	/**
	 * S3 key of the rendered landing HTML; null while pending. Entity-internal — the
	 * write-back worker sets it and `htmlKey != null` is the "landing ready" signal;
	 * it is NOT serialized into the offer DTO (no API consumer reads the raw key).
	 */
	htmlKey: string | null
	/** S3 key of the rendered PDF; null while pending. Entity-internal, not in the DTO (see `htmlKey`). */
	pdfKey: string | null
	/** Public audit code of the PDF; null while pending. */
	pdfCode: string | null
	/**
	 * Fingerprint of the Object-Locked artefact versions the keys point at (TO-115):
	 * the S3 version id and the lowercase-hex SHA-256 of the stored bytes. Null until
	 * written back. Entity-internal, not in the DTO (see `htmlKey`).
	 */
	htmlS3VersionId: string | null
	htmlSha256: string | null
	pdfS3VersionId: string | null
	pdfSha256: string | null
	/** The published version (TO-464): null until the first send completes. */
	currentVersion: number | null
	/** The linked notification template (TO-140); null when none. Never set together with `notificationBody`. */
	notificationTemplateId: string | null
	/** The custom notification message, Liquid source (TO-140); null when none. */
	notificationBody: string | null
	/** The render of the published version; null until the first send completes. */
	publishedRenderId: string | null
	/**
	 * S3 key of the published version's landing HTML — the only landing served
	 * publicly. Entity-internal, not in the DTO (see `htmlKey`).
	 */
	publishedHtmlKey: string | null
	/** Version the in-flight render is producing; non-null ⇔ a render is pending. */
	pendingRenderVersion: number | null
	/** The user whose send / edit requested the pending render. Entity-internal. */
	pendingRequestedBy: string | null
	/** The pending render's frozen payload, recorded on its snapshot. Entity-internal. */
	pendingPayload: Record<string, unknown> | null
	/** The last render failure; null when none or once a new render is requested. */
	renderFailure: OfferRenderFailure | null
	/** The minted public short code; null when the mint has not (yet) succeeded. */
	shortCode: string | null
	sentAt: Date | null
	openedAt: Date | null
	expiresAt: Date | null
	createdAt: Date
	updatedAt: Date
	/** The offer's products, in offer order (position ascending). */
	productIds: string[]
}
